<script lang="ts" setup>
import "../public/fonts/stylesheet.scss";
import { useRoute } from "vue-router";
import { computed } from "vue";
import PaymentModal from "@/components/PaymentModal.vue";
import LanguageSwitcher from "@/components/base/LanguageSwitcher.vue";
import { useStore } from "@/store";
import BaseModal from "@/components/base/BaseModal.vue";
import { ModalConfigStruct } from "@/struct/ui/UIModalStruct";
import { useAppViewport } from "@/composables/useAppViewport";
import { useRouteTransition } from "@/composables/useRouteTransition";

const route = useRoute();
const store = useStore();

// Держит --app-height / --keyboard-inset в актуальном состоянии для всего приложения.
useAppViewport();

// Направление перехода между страницами: вперёд / назад.
const routeTransition = useRouteTransition();

const layoutComponent = computed(() => {
  return route.meta.layout || "main-layout";
});

const showPaymentModal = computed(() => store.state.system.showPaymentModal);

const closePaymentModal = () => {
  store.dispatch("system/togglePaymentModal", false);
};

const brandId = computed(() => store.state.brand.operatorSource);

const modalConfig = computed<ModalConfigStruct | null>(
  () => store.state.system.activeConfig
);

const closeModal = () => store.dispatch("system/closeModal");

const handleAction = () => {
  if (modalConfig.value?.onAction) modalConfig.value.onAction();
  closeModal();
};

const handleSecondaryAction = () => {
  if (modalConfig.value?.onSecondaryAction)
    modalConfig.value.onSecondaryAction();
  closeModal();
};
</script>

<template>
  <div class="root" :data-theme="brandId">
    <LanguageSwitcher :is-visible="false" />
    <component :is="layoutComponent">
      <!--
        Обёртка обязательна: часть страниц (Account, AddonsPage, PaymentPage,
        FeedbackView) рендерит фрагмент — саму страницу и <Teleport> с модалками.
        У <Transition mode="out-in"> корень должен быть одним элементом, иначе
        переход не завершается и следующая страница просто не монтируется.
        Свой <div> гарантирует единственный корень для любой страницы.
      -->
      <router-view v-slot="{ Component, route }">
        <Transition :name="routeTransition" mode="out-in">
          <div :key="route.path" class="page-transition">
            <component :is="Component" />
          </div>
        </Transition>
      </router-view>
    </component>
    <Transition name="fade">
      <BaseModal
        v-if="modalConfig"
        v-bind="modalConfig"
        @close="closeModal"
        @action="handleAction"
        @secondary-action="handleSecondaryAction"
      />
    </Transition>
    <PaymentModal :show-modal="showPaymentModal" @close="closePaymentModal" />
  </div>
</template>

<style lang="scss">
@import "assets/style/main";

.root {
  position: relative;
  min-height: var(--app-height);
}
</style>

<style lang="scss" scoped>
@import "assets/style/themes";

.root {
  @include render-themes;
}
</style>
